// ── useCompanyAndSettlement — 외부 회사·정산 설정 상태 & API ─────────────────
import { useState } from 'react';
import { useToast } from '../../../components/Toast';
import { apiClient } from '../../../api/apiClient';

/**
 * @param {object} deps
 * @param {Array}  deps.categories - useTicketManagement의 categories (정산 설정 카테고리 변경 시 사용)
 * @param {Array}  deps.tickets    - useTicketManagement의 tickets    (정산 설정 상품 목록 파생 시 사용)
 */
export function useCompanyAndSettlement({ categories, tickets }) {
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);

  // ── 외부 회사 관리 ──────────────────────────────────────────────────────────
  const [extCompanies,      setExtCompanies]      = useState([]);
  const [extCompanyLoading, setExtCompanyLoading] = useState(false);
  const [extEditId,         setExtEditId]         = useState(null);   // 수정 중인 회사 id
  const [extEditFields,     setExtEditFields]     = useState({});

  // ── 외부 회사별 일일콜 상품 설정 (ext_product_{CODE}) ─────────────────────────
  // { [code]: { name, isCategory, loading, saving } }
  const [extProductSettings, setExtProductSettings] = useState({});

  async function fetchExtProductSetting(code) {
    setExtProductSettings((prev) => ({ ...prev, [code]: { ...(prev[code] || {}), loading: true } }));
    const r = await apiClient.get(`/api/admin/ext/${code}/tm-calls/external-product`);
    setExtProductSettings((prev) => ({
      ...prev,
      [code]: {
        name:       r.ok ? (r.data?.value || '') : (prev[code]?.name || ''),
        isCategory: r.ok ? Boolean(r.data?.is_category) : (prev[code]?.isCategory || false),
        loading: false, saving: false,
      },
    }));
  }

  async function saveExtProductSetting(code) {
    // is_category는 상품 관리 카테고리 설정(call_is_category)에서 파생 — 여기서 전송 불필요
    const cur = extProductSettings[code] || {};
    setExtProductSettings((prev) => ({ ...prev, [code]: { ...cur, saving: true } }));
    const r = await apiClient.put(`/api/admin/ext/${code}/tm-calls/external-product`, { name: cur.name });
    // 저장 후 서버에서 파생된 is_category 반영
    const isCategory = r.ok ? Boolean(r.data?.is_category) : cur.isCategory;
    setExtProductSettings((prev) => ({ ...prev, [code]: { ...cur, saving: false, isCategory } }));
    if (!r.ok) { showToast(r.data?.message || '저장 실패', 'error'); return; }
    showToast('일일콜 카테고리 설정이 저장됐습니다.', 'success');
  }

  function setExtProductField(code, field, value) {
    setExtProductSettings((prev) => ({ ...prev, [code]: { ...(prev[code] || {}), [field]: value } }));
  }

  // ── 정산 설정 ────────────────────────────────────────────────────────────────
  const [settlementAllCategories, setSettlementAllCategories] = useState([]);
  const [settlementCompanies,     setSettlementCompanies]     = useState([]);
  const [settlementDrafts,        setSettlementDrafts]        = useState({});
  const [settlementLoading,       setSettlementLoading]       = useState(false);

  // ── 콤마 포맷 헬퍼 ────────────────────────────────────────────────────────────
  function toCommaStr(val) {
    const n = parseInt(String(val).replace(/,/g, ''), 10);
    return isNaN(n) ? '0' : n.toLocaleString('ko-KR');
  }
  function stripComma(str) { return String(str).replace(/,/g, ''); }

  // ── 외부 회사 API ────────────────────────────────────────────────────────────
  async function fetchExtCompanies() {
    setExtCompanyLoading(true);
    const r = await apiClient.get('/api/admin/external-companies');
    setExtCompanyLoading(false);
    if (!r.ok) { showToast('외부 회사 목록을 불러오지 못했습니다.', 'error'); return; }
    setExtCompanies(Array.isArray(r.data?.list) ? r.data.list : []);
  }

  async function saveExtCompany(id) {
    if (!extEditFields.name?.trim()) { showToast('표시명은 필수입니다.', 'error'); return; }
    const newCodeRaw = String(extEditFields.code || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    const company = extCompanies.find(c => c.id === id);
    if (!newCodeRaw) { showToast('코드는 필수입니다.', 'error'); return; }
    setSaving(true);
    const originalEf = company?.enabled_features || {};
    const newEf = extEditFields.enabled_features !== undefined
      ? extEditFields.enabled_features
      : originalEf;
    const efChanged = JSON.stringify(originalEf) !== JSON.stringify(newEf);
    const body = {
      name: extEditFields.name.trim(),
      company_name: (extEditFields.company_name || '').trim() || null,
    };
    if (extEditFields.enabled_features !== undefined) body.enabled_features = newEf;
    // 코드가 바뀐 경우 cascade 업데이트를 위해 new_code 포함
    if (newCodeRaw !== company?.code?.toUpperCase()) body.new_code = newCodeRaw;
    const r = await apiClient.put(`/api/admin/external-companies/${id}`, body);
    if (!r.ok) { setSaving(false); showToast(r.data?.message || '수정 실패', 'error'); return; }

    // 수수료율도 함께 저장 (수식 유효성 검증 후 설정된 항목만)
    setSaving(false);
    if (efChanged) {
      // 기능 활성화 변경 → 사이드바 탭 갱신을 위해 페이지 새로고침
      showToast('기능 설정이 변경됐습니다. 탭 메뉴를 갱신합니다...', 'success');
      setTimeout(() => window.location.reload(), 900);
    } else {
      showToast('수정됐습니다.', 'success');
      setExtEditId(null);
      setExtEditFields({});
      fetchExtCompanies();
    }
  }

  // enabled_features의 특정 키 토글 (외부 회사 수정 모드에서 사용)
  function toggleExtFeature(featureKey) {
    setExtEditFields((f) => ({
      ...f,
      enabled_features: {
        ...f.enabled_features,
        [featureKey]: !f.enabled_features?.[featureKey],
      },
    }));
  }

  async function toggleExtCompanyActive(company) {
    setSaving(true);
    const r = await apiClient.put(`/api/admin/external-companies/${company.id}`, { is_active: !company.is_active });
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '변경 실패', 'error'); return; }
    fetchExtCompanies();
  }

  // ── 정산 설정 API ────────────────────────────────────────────────────────────
  async function fetchSettlementSettings() {
    setSettlementLoading(true);
    const r = await apiClient.get('/api/admin/settings/settlement');
    setSettlementLoading(false);
    if (!r.ok) { showToast('정산 설정을 불러오지 못했습니다.', 'error'); return; }
    const allCats   = Array.isArray(r.data?.allCategories) ? r.data.allCategories : [];
    const companies = Array.isArray(r.data?.companies)     ? r.data.companies     : [];
    setSettlementAllCategories(allCats);
    setSettlementCompanies(companies);
    // 회사별 draft 초기화
    const drafts = {};
    for (const co of companies) {
      const d = {};
      co.products.forEach(({ id, price, sellingPrice }) => {
        d[`jeju_${id}`]         = toCommaStr(price        || 0);  // 마케터수취금액
        d[`selling_price_${id}`] = toCommaStr(sellingPrice || 0); // 판매가
      });
      co.rates.forEach(({ key, value }) => { d[key] = String(value ?? ''); });
      d['delivery_fee']              = toCommaStr(co.deliveryFee ?? 5000);
      d['consultant_company_share']  = String(co.consultantCompanyShare  ?? 50); d['consultant_marketer_share'] = String(co.consultantMarketerShare ?? 50);
      drafts[co.code] = d;
    }
    setSettlementDrafts(drafts);
  }

  // 카테고리 드롭다운 변경 시 — 로컬 tickets에서 상품 목록 파생 (서버 재요청 없음)
  function handleSettlementCategoryChange(companyCode, newCatId) {
    const cat = newCatId ? categories.find((c) => c.id === Number(newCatId)) : null;
    const catTickets = cat
      ? tickets.filter((t) => t.category === cat.name && t.is_active !== false)
               .sort((a, b) => a.name.localeCompare(b.name))
      : [];
    const products = catTickets.map((t) => ({ id: t.id, name: t.name, price: 0, sellingPrice: 0 }));
    setSettlementCompanies((prev) => prev.map((co) =>
      co.code !== companyCode ? co : { ...co, categoryId: newCatId || null, products }
    ));
    setSettlementDrafts((prev) => {
      const d = { ...prev[companyCode] };
      catTickets.forEach(({ id }) => {
        if (!d[`jeju_${id}`])          d[`jeju_${id}`]          = '0'; // 마케터수취금액
        if (!d[`selling_price_${id}`]) d[`selling_price_${id}`] = '0'; // 판매가
      });
      return { ...prev, [companyCode]: d };
    });
  }

  // 회사별 정산 설정 저장 (effectiveFrom: 'YYYY-MM-DD' — 생략 시 오늘 날짜로 즉시 적용)
  async function saveSettlementSettings(companyCode, effectiveFrom = null) {
    const company = settlementCompanies.find((co) => co.code === companyCode);
    if (!company) return;
    const draft = settlementDrafts[companyCode] || {};
    // 마케터수취금액 (price_N)
    const prices = company.products.map(({ id }) => {
      const v = parseFloat(stripComma(draft[`jeju_${id}`] ?? '0'));
      return { id, price: isNaN(v) || v < 0 ? 0 : v };
    });
    // 판매가 (selling_price_N)
    const sellingPrices = company.products.map(({ id }) => {
      const v = parseFloat(stripComma(draft[`selling_price_${id}`] ?? '0'));
      return { id, price: isNaN(v) || v < 0 ? 0 : v };
    });
    const deliveryFeeRaw = parseFloat(stripComma(draft['delivery_fee'] ?? '0'));
    const deliveryFeeVal = isNaN(deliveryFeeRaw) || deliveryFeeRaw < 0 ? 5000 : Math.round(deliveryFeeRaw);
    const ccShare = Math.min(100, Math.max(0, parseFloat(draft['consultant_company_share']  ?? '50') || 50));
    const cmShare = Math.min(100, Math.max(0, parseFloat(draft['consultant_marketer_share'] ?? '50') || 50));
    setSaving(true);
    const r = await apiClient.put('/api/admin/settings/settlement', {
      companyCode, categoryId: company.categoryId, prices, sellingPrices, deliveryFee: deliveryFeeVal,
      consultantCompanyShare: ccShare, consultantMarketerShare: cmShare,
      ...(effectiveFrom && { effectiveFrom }),
    });
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '저장 실패', 'error'); return; }
    showToast(`${company.name} 정산 설정이 저장되었습니다.`, 'success');
    // 서버 응답으로 전체 회사 목록 재동기화
    const newCompanies = Array.isArray(r.data?.companies) ? r.data.companies : settlementCompanies;
    setSettlementCompanies(newCompanies);
    const newDrafts = {};
    for (const co of newCompanies) {
      const d = {};
      co.products.forEach(({ id, price, sellingPrice }) => {
        d[`jeju_${id}`]          = toCommaStr(price        || 0); // 마케터수취금액
        d[`selling_price_${id}`] = toCommaStr(sellingPrice || 0); // 판매가
      });
      co.rates.forEach(({ key, value }) => { d[key] = String(value ?? ''); });
      d['delivery_fee']              = toCommaStr(co.deliveryFee ?? 5000);
      d['consultant_company_share']  = String(co.consultantCompanyShare  ?? 50); d['consultant_marketer_share'] = String(co.consultantMarketerShare ?? 50);
      newDrafts[co.code] = d;
    }
    setSettlementDrafts(newDrafts);
  }

  return {
    saving,
    stripComma,
    // 외부 회사
    extCompanies, extCompanyLoading,
    extEditId, setExtEditId, extEditFields, setExtEditFields,
    fetchExtCompanies, saveExtCompany,
    toggleExtFeature, toggleExtCompanyActive,
    // 일일콜 상품 설정 (ext_product_{CODE} / _is_category)
    extProductSettings,
    fetchExtProductSetting, saveExtProductSetting, setExtProductField,
    // 정산 설정
    settlementAllCategories, settlementCompanies, settlementDrafts, setSettlementDrafts,
    settlementLoading,
    fetchSettlementSettings, handleSettlementCategoryChange, saveSettlementSettings,
  };
}
